import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import PostAddIcon from '@mui/icons-material/PostAdd';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import apiClient from '../utils/axios';
import { formatNumber } from '../utils/formatUtils';

const AutoTradingSector = () => {
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [searchingStock, setSearchingStock] = useState(false);
  const [foundStockCode, setFoundStockCode] = useState('');
  const [foundStockName, setFoundStockName] = useState('');
  const [sectors, setSectors] = useState([]);
  const [sectorName, setSectorName] = useState('');
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [sectorForm, setSectorForm] = useState({
    name: '',
    description: '',
    parent_id: '',
    is_active: true
  });
  const [sectorList, setSectorList] = useState([]);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [stockForm, setStockForm] = useState({
    code: '',
    name: '',
    sector_id: '',
    priority: 1,
    latest_up: ''
  });

  // 섹터별 관심종목 목록 조회 (최근 10거래일 종가 포함)
  const fetchWatchlist = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const response = await apiClient.get('/stock-sectors?with_prices=true&days=10');
      setWatchlist(response.data || []);
    } catch (error) {
      console.error('[섹터별 관심종목] 목록 조회 실패:', error);
      let message = '섹터별 관심종목 목록을 불러오는 중 오류가 발생했습니다.';
      if (error.response?.status === 401) {
        message = '로그인이 필요합니다.';
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      }
      setErrorMessage(message);
      setWatchlist([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 종목명으로 종목 검색 (stockList.json에서 검색)
  const searchStockByName = useCallback(async (stockName) => {
    if (!stockName || stockName.length < 2) {
      return null;
    }

    try {
      setSearchingStock(true);
      const response = await apiClient.post('/watchlist/search', {
        stockName: stockName.trim()
      });
      
      if (response.data?.stockCode && response.data?.stockName) {
        return {
          stockCode: response.data.stockCode,
          stockName: response.data.stockName
        };
      }
      
      return null;
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('[종목 검색] 실패:', error.message);
      }
      return null;
    } finally {
      setSearchingStock(false);
    }
  }, []);

  // 종목명 변경 시 자동 검색
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchName.trim().length >= 2) {
        const result = await searchStockByName(searchName.trim());
        if (result) {
          setFoundStockCode(result.stockCode);
          setFoundStockName(result.stockName);
        } else {
          setFoundStockCode('');
          setFoundStockName('');
        }
      } else {
        setFoundStockCode('');
        setFoundStockName('');
      }
    }, 500); // 500ms 디바운스

    return () => clearTimeout(timer);
  }, [searchName, searchStockByName]);

  // 종목 추가 다이얼로그 열기
  const handleOpenStockDialog = useCallback(async () => {
    const trimmedName = searchName.trim();
    if (!trimmedName) {
      setErrorMessage('종목명을 입력하세요.');
      return;
    }

    // 이미 검색된 종목코드가 있으면 사용, 없으면 검색
    let stockCode = foundStockCode;
    let stockName = foundStockName || trimmedName;
    
    if (!stockCode) {
      const result = await searchStockByName(trimmedName);
      if (!result?.stockCode) {
        setErrorMessage(`종목명 "${trimmedName}"에 대한 종목 정보를 찾을 수 없습니다. 올바른 종목명을 입력해주세요.`);
        return;
      }
      stockCode = result.stockCode;
      stockName = result.stockName;
    }

    setStockForm({
      code: stockCode,
      name: stockName,
      sector_id: '',
      priority: 1,
      latest_up: ''
    });
    setEditingStock(null);
    setStockDialogOpen(true);
  }, [searchName, foundStockCode, foundStockName, searchStockByName]);

  // 종목 수정 다이얼로그 열기 (현재 미사용)
  // eslint-disable-next-line no-unused-vars
  const handleOpenEditStockDialog = useCallback((stock) => {
    setEditingStock(stock);
    setStockForm({
      code: stock.stock_code || stock.code || '',
      name: stock.stock_name || stock.name || '',
      sector_id: stock.sector_id || '',
      priority: stock.priority !== undefined ? stock.priority : 1,
      latest_up: stock.latest_up || ''
    });
    setStockDialogOpen(true);
  }, []);

  // 종목 저장 (추가/수정)
  const handleSaveStock = useCallback(async () => {
    if (!stockForm.code.trim() || !stockForm.sector_id) {
      setErrorMessage('종목코드와 섹터는 필수입니다.');
      return;
    }

    try {
      setErrorMessage(null);
      
      const stockSectorData = {
        stock_code: stockForm.code.trim(),
        sector_id: stockForm.sector_id,
        priority: stockForm.priority || 1,
        latest_up: stockForm.latest_up || null
      };

      if (editingStock) {
        // 수정
        await apiClient.put(`/stock-sectors/${stockForm.code.trim()}/${stockForm.sector_id}`, stockSectorData);
      } else {
        // 추가
        await apiClient.post('/stock-sectors', stockSectorData);
      }

      // 입력 필드 초기화
      setSearchName('');
      setFoundStockCode('');
      setFoundStockName('');
      setStockDialogOpen(false);
      setEditingStock(null);
      setStockForm({
        code: '',
        name: '',
        sector_id: '',
        priority: 1,
        latest_up: ''
      });

      // 목록 갱신
      await fetchWatchlist();
    } catch (error) {
      const message = error.response?.data?.error || (editingStock ? '종목-섹터 매핑 수정 중 오류가 발생했습니다.' : '종목-섹터 매핑 추가 중 오류가 발생했습니다.');
      setErrorMessage(message);
    }
  }, [stockForm, editingStock, fetchWatchlist]);

  // 종목-섹터 매핑 삭제
  const handleDeleteStock = useCallback(async (stockCode, sectorId, stockName) => {
    const displayName = stockName || stockCode;
    if (!window.confirm(`정말 "${displayName}"을(를) 이 섹터에서 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await apiClient.delete(`/stock-sectors/${stockCode}/${sectorId}`);
      await fetchWatchlist();
    } catch (error) {
      const message = error.response?.data?.error || '종목-섹터 매핑 삭제 중 오류가 발생했습니다.';
      setErrorMessage(message);
    }
  }, [fetchWatchlist]);

  // 섹터 목록 조회
  const fetchSectors = useCallback(async () => {
    try {
      const response = await apiClient.get('/sectors');
      setSectorList(response.data || []);
    } catch (error) {
      console.error('[섹터] 목록 조회 실패:', error);
    }
  }, []);

  // 섹터 추가 다이얼로그 열기
  const handleOpenSectorDialog = useCallback(() => {
    if (!sectorName.trim()) {
      setErrorMessage('섹터명을 입력하세요.');
      return;
    }
    setSectorForm({
      name: sectorName.trim(),
      description: '',
      parent_id: '',
      is_active: true
    });
    setSectorDialogOpen(true);
  }, [sectorName]);

  // 섹터 수정 다이얼로그 열기
  const handleOpenEditSectorDialog = useCallback((sector) => {
    setEditingSector(sector);
    setSectorForm({
      name: sector.name || '',
      description: sector.description || '',
      parent_id: sector.parent_id || '',
      is_active: sector.is_active !== undefined ? sector.is_active : true
    });
    setSectorDialogOpen(true);
  }, []);

  // 섹터 저장 (추가/수정)
  const handleSaveSector = useCallback(async () => {
    if (!sectorForm.name.trim()) {
      setErrorMessage('섹터명은 필수입니다.');
      return;
    }

    try {
      setErrorMessage(null);
      
      if (editingSector) {
        // 수정
        await apiClient.put(`/sectors/${editingSector.id}`, {
          name: sectorForm.name.trim(),
          description: sectorForm.description.trim() || null,
          parent_id: sectorForm.parent_id || null,
          is_active: sectorForm.is_active
        });
      } else {
        // 추가
        await apiClient.post('/sectors', {
          name: sectorForm.name.trim(),
          description: sectorForm.description.trim() || null,
          parent_id: sectorForm.parent_id || null,
          is_active: sectorForm.is_active
        });
      }

      // 입력 필드 초기화
      setSectorName('');
      setSectorDialogOpen(false);
      setEditingSector(null);
      setSectorForm({
        name: '',
        description: '',
        parent_id: '',
        is_active: true
      });

      // 섹터 목록 갱신
      await fetchSectors();
    } catch (error) {
      const message = error.response?.data?.error || (editingSector ? '섹터 수정 중 오류가 발생했습니다.' : '섹터 추가 중 오류가 발생했습니다.');
      setErrorMessage(message);
    }
  }, [sectorForm, editingSector, fetchSectors]);

  // 초기 로드
  useEffect(() => {
    fetchWatchlist();
    fetchSectors();
  }, [fetchWatchlist, fetchSectors]);

  return (
    <Container maxWidth="lg" sx={{ mb: 4 }}>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {/* 섹터별 관심종목 목록 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CategoryIcon sx={{ fontSize: '1.35rem', color: 'text.secondary' }} aria-hidden />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
            섹터별 관심종목
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : watchlist.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            등록된 섹터별 종목이 없습니다.
          </Box>
        ) : (
          <Grid container spacing={2}>
            {watchlist.map((sectorData) => {
              return sectorData.stocks.map((stock) => {
                // 최근 10거래일 데이터 표시 (최신일부터)
                // 모든 날짜를 포함하되, 가격이 없는 날짜도 표시
                console.log(`[프론트엔드] ${stock.stock_code || stock.stock_name} 가격 데이터 개수:`, stock.prices?.length || 0);
                const sortedPrices = [...(stock.prices || [])]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .slice(0, 15);
                console.log(`[프론트엔드] ${stock.stock_code || stock.stock_name} 정렬 후 데이터 개수:`, sortedPrices.length);
                
                // 전일대비 변동 계산
                const calculateChange = (currentPrice, prevPrice) => {
                  if (!currentPrice || !prevPrice) return null;
                  return currentPrice - prevPrice;
                };
                
                return (
                  <Grid item xs={12} sm={6} md={3} key={`${sectorData.sector_id}_${stock.stock_code}`}>
                    <Paper
                      sx={{
                        p: 2,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {/* 섹터명 */}
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: 'primary.main',
                          mb: 1,
                          fontSize: '0.875rem'
                        }}
                      >
                        {sectorData.sector_name}
                      </Typography>
                      
                      {/* 종목명과 삭제 버튼 */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: '1rem'
                          }}
                        >
                          {stock.stock_name || stock.stock_code}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteStock(stock.stock_code, sectorData.sector_id, stock.stock_name)}
                          sx={{
                            color: 'error.main',
                            '&:hover': {
                              backgroundColor: 'error.light',
                              color: 'error.dark'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      {/* 날짜별 데이터 */}
                      <Box sx={{ flexGrow: 1 }}>
                        {sortedPrices.length > 0 ? (
                          sortedPrices.map((priceData, idx) => {
                            const dateObj = new Date(priceData.date);
                            const month = dateObj.getMonth() + 1;
                            const day = dateObj.getDate();
                            const dateStr = `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                            
                            // 전일대비 변동 (키움 API의 pred_pre 필드 사용, 없으면 계산)
                            let change = null;
                            if (priceData.predPre !== null && priceData.predPre !== undefined) {
                              // 키움 API에서 제공하는 전일대비 값 사용
                              change = priceData.predPre;
                            } else if (idx < sortedPrices.length - 1) {
                              // pred_pre가 없으면 현재 날짜의 전일 가격과 비교하여 계산
                              const prevPrice = sortedPrices[idx + 1].price;
                              change = calculateChange(priceData.price, prevPrice);
                            }
                            
                            return (
                              <Box 
                                key={priceData.date}
                                sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: idx < sortedPrices.length - 1 ? 1 : 0,
                                  width: '100%'
                                }}
                              >
                                <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: '30px' }}>
                                  {dateStr}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'nowrap', flex: 1 }}>
                                  {priceData.price !== null && priceData.price !== undefined ? (
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'right', minWidth: '50px' }}>
                                      {formatNumber(priceData.price)}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', textAlign: 'right', minWidth: '50px' }}>
                                      -
                                    </Typography>
                                  )}
                                  {priceData.highPrice && (
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 'bold',
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                        textAlign: 'right',
                                        minWidth: '55px'
                                      }}
                                    >
                                      H:{formatNumber(priceData.highPrice)}
                                    </Typography>
                                  )}
                                  {change !== null && (
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 'bold',
                                        color: change >= 0 ? 'error.main' : 'primary.main',
                                        textAlign: 'right',
                                        minWidth: '50px'
                                      }}
                                    >
                                      {change >= 0 ? '+' : ''}{formatNumber(change)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            데이터 없음
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              });
            })}
          </Grid>
        )}
      </Paper>

      {/* 섹터 추가 및 종목 추가 */}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* 섹터 추가 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PostAddIcon sx={{ fontSize: '1.35rem', color: 'text.secondary' }} aria-hidden />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                섹터추가
              </Typography>
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={8}>
                <TextField
                  label="섹터명"
                  fullWidth
                  value={sectorName}
                  onChange={(e) => setSectorName(e.target.value)}
                  placeholder="예: IT/소프트웨어"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (sectorName.trim()) {
                        setSectors([...sectors, sectorName.trim()]);
                        setSectorName('');
                      }
                    }
                  }}
                  helperText="섹터명을 입력하세요."
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleOpenSectorDialog}
                  disabled={!sectorName.trim()}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  추가
                </Button>
              </Grid>
            </Grid>
            {sectorList.length > 0 && (
              <TableContainer sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover', '& .MuiTableCell-root': { py: 1.5 } }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>섹터명</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>설명</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>상태</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>작업</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sectorList.map((sector) => (
                      <TableRow key={sector.id} hover>
                        <TableCell>{sector.name}</TableCell>
                        <TableCell>{sector.description || '-'}</TableCell>
                        <TableCell align="center">
                          <Box 
                            display="flex" 
                            alignItems="center" 
                            justifyContent="center"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleOpenEditSectorDialog(sector)}
                          >
                            <Avatar
                              sx={{
                                width: 12,
                                height: 12,
                                bgcolor: sector.is_active ? 'success.main' : 'grey.400',
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="error"
                            onClick={async () => {
                              if (window.confirm(`정말 "${sector.name}" 섹터를 삭제하시겠습니까?`)) {
                                try {
                                  await apiClient.delete(`/sectors/${sector.id}`);
                                  await fetchSectors();
                                } catch (error) {
                                  const message = error.response?.data?.error || '섹터 삭제 중 오류가 발생했습니다.';
                                  setErrorMessage(message);
                                }
                              }
                            }}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>

          {/* 종목 검색 및 추가 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PlaylistAddIcon sx={{ fontSize: '1.35rem', color: 'text.secondary' }} aria-hidden />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                종목추가
              </Typography>
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={8}>
                <TextField
                  label="종목명"
                  fullWidth
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="예: 삼성전자"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleOpenStockDialog();
                    }
                  }}
                  InputProps={{
                    endAdornment: searchingStock ? (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ) : foundStockCode ? (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', mr: 1 }}>
                          {foundStockCode}
                        </Typography>
                      </InputAdornment>
                    ) : null
                  }}
                  helperText={
                    searchingStock 
                      ? '종목 정보를 검색하는 중...' 
                      : foundStockCode 
                        ? `종목명: ${foundStockName || searchName}` 
                        : '종목명을 입력하면 자동으로 종목코드가 검색됩니다.'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleOpenStockDialog}
                  disabled={loading || !searchName.trim()}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  추가
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* 섹터 추가/수정 다이얼로그 */}
      <Dialog
        open={sectorDialogOpen}
        onClose={() => {
          setSectorDialogOpen(false);
          setEditingSector(null);
          setSectorForm({
            name: '',
            description: '',
            parent_id: '',
            is_active: true
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingSector ? '섹터 수정' : '섹터 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="섹터명 *"
              fullWidth
              value={sectorForm.name}
              onChange={(e) => setSectorForm({ ...sectorForm, name: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              label="설명"
              fullWidth
              multiline
              rows={3}
              value={sectorForm.description}
              onChange={(e) => setSectorForm({ ...sectorForm, description: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="섹터에 대한 설명을 입력하세요."
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>상위 섹터</InputLabel>
              <Select
                value={sectorForm.parent_id || ''}
                onChange={(e) => setSectorForm({ ...sectorForm, parent_id: e.target.value || null })}
                label="상위 섹터"
              >
                <MenuItem value="">없음</MenuItem>
                {sectorList
                  .filter(s => !editingSector || s.id !== editingSector.id) // 자기 자신 제외
                  .map((sector) => (
                    <MenuItem key={sector.id} value={sector.id}>
                      {sector.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={sectorForm.is_active}
                  onChange={(e) => setSectorForm({ ...sectorForm, is_active: e.target.checked })}
                />
              }
              label="활성화"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setSectorDialogOpen(false);
              setEditingSector(null);
              setSectorForm({
                name: '',
                description: '',
                parent_id: '',
                is_active: true
              });
            }}
          >
            취소
          </Button>
          <Button onClick={handleSaveSector} variant="contained" color="primary">
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 종목 추가/수정 다이얼로그 */}
      <Dialog
        open={stockDialogOpen}
        onClose={() => {
          setStockDialogOpen(false);
          setEditingStock(null);
          setStockForm({
            code: '',
            name: '',
            sector_id: '',
            priority: 1,
            latest_up: ''
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingStock ? '종목-섹터 매핑 수정' : '종목-섹터 매핑 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="종목코드 *"
              fullWidth
              value={stockForm.code}
              disabled
              sx={{ mb: 2 }}
              required
            />
            <TextField
              label="종목명"
              fullWidth
              value={stockForm.name}
              disabled
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel>섹터 *</InputLabel>
              <Select
                value={stockForm.sector_id || ''}
                onChange={(e) => setStockForm({ ...stockForm, sector_id: e.target.value })}
                label="섹터 *"
              >
                {sectorList.map((sector) => (
                  <MenuItem key={sector.id} value={sector.id}>
                    {sector.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="우선순위"
              type="number"
              fullWidth
              value={stockForm.priority}
              onChange={(e) => setStockForm({ ...stockForm, priority: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1 }}
              sx={{ mb: 2 }}
              helperText="1: 주력, 2~: 부가"
            />
            <TextField
              label="최신 업데이트 날짜"
              type="date"
              fullWidth
              value={stockForm.latest_up}
              onChange={(e) => setStockForm({ ...stockForm, latest_up: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 2 }}
              helperText="yyyy-mm-dd 형식"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setStockDialogOpen(false);
              setEditingStock(null);
              setStockForm({
                code: '',
                name: '',
                sector_id: '',
                priority: 1,
                latest_up: ''
              });
            }}
          >
            취소
          </Button>
          <Button onClick={handleSaveStock} variant="contained" color="primary">
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AutoTradingSector;

